<div class="flex flex-col flex-1 min-h-0 overflow-hidden">
  <div class="flex-1 min-h-0 overflow-y-auto">
    @if (packagesResource.isLoading()) {
      <div class="flex flex-col items-center justify-center h-full gap-3 text-surface-400 p-8 text-center">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
        <p class="text-body-sm m-0">Loading packages...</p>
      </div>
    } @else if (packagesHasError()) {
      <div class="flex flex-col items-center justify-center h-full gap-3 p-8 text-center">
        <i class="pi pi-times-circle text-red-600" style="font-size: 2rem"></i>
        <p class="text-body-sm-500 text-surface-700 m-0">Could not load packages.</p>
        <p class="text-body-sm text-surface-500 m-0">
          Something went wrong while loading packages for this model and elevation.
        </p>
        <p-button label="Retry" icon="pi pi-refresh" size="small" [outlined]="true" (onClick)="retryPackages()" />
      </div>
    } @else if (!packages().length) {
      <div class="flex flex-col items-center justify-center h-full gap-3 text-surface-400 p-8 text-center">
        <i class="bi bi-box" style="font-size: 3rem"></i>
        <p class="text-body-sm m-0">No packages available for this model and elevation.</p>
      </div>
    } @else {
      <p-table [value]="packages()" styleClass="p-datatable-sm">
        <ng-template #header>
          <tr>
            <th pSortableColumn="name">
              <div class="flex items-center gap-1">
                Name
                <p-sortIcon field="name" />
              </div>
            </th>
            <th>Project</th>
            <th pSortableColumn="modelName">
              <div class="flex items-center gap-1">
                Model/Elevation
                <p-sortIcon field="modelName" />
              </div>
            </th>
            <th pSortableColumn="itemCount" class="text-center">
              <div class="flex items-center justify-center gap-1">
                Total Options
                <p-sortIcon field="itemCount" />
              </div>
            </th>
            <th pSortableColumn="totalPrice" class="text-right">
              <div class="flex items-center justify-end gap-1">
                Total Cost
                <p-sortIcon field="totalPrice" />
              </div>
            </th>
            <th pSortableColumn="discountPercentage" class="text-right">
              <div class="flex items-center justify-end gap-1">
                Discount %
                <p-sortIcon field="discountPercentage" />
              </div>
            </th>
            <th class="text-right"></th>
          </tr>
        </ng-template>
        <ng-template #body let-pkg>
          <tr class="cursor-pointer hover:bg-surface-50" (click)="openPackageDialog(pkg)">
            <td class="text-body-md-600 text-surface-700">{{ pkg.name }}</td>
            <td class="text-surface-600">{{ pkg.project?.name || '-' }}</td>
            <td class="text-surface-600">{{ modelElevationLabel(pkg) }}</td>
            <td class="text-center">{{ pkg.itemCount || 0 }}</td>
            <td class="text-right text-emerald-700 text-body-md-600">
              @if (packageHasDiscount(pkg)) {
                <div class="flex flex-col items-end">
                  <span class="text-surface-400 line-through font-normal text-body-sm">{{
                    pkg.totalPrice | currencyFormat
                  }}</span>
                  <span>{{ discountedPackageTotal(pkg) | currencyFormat }}</span>
                </div>
              } @else {
                {{ pkg.totalPrice | currencyFormat }}
              }
            </td>
            <td class="text-right">{{ pkg.discountPercentage ?? 0 }}%</td>
            <td class="text-right">
              <div class="flex items-center justify-end gap-2" (click)="$event.stopPropagation()">
                <p-button
                  icon="pi pi-eye"
                  size="small"
                  severity="secondary"
                  [outlined]="true"
                  pTooltip="Preview"
                  tooltipPosition="top"
                  aria-label="Preview"
                  (onClick)="openPackageDialog(pkg)"
                />
                <p-button
                  icon="pi pi-plus"
                  size="small"
                  pTooltip="Add Package"
                  tooltipPosition="top"
                  aria-label="Add Package"
                  (onClick)="openPackageDialog(pkg)"
                />
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    }
  </div>
</div>

<p-dialog
  [visible]="packageDialogVisible()"
  (visibleChange)="$event || closePackageDialog()"
  [modal]="true"
  [closable]="false"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '752px', maxWidth: '95vw' }"
  [baseZIndex]="11000"
  appendTo="body"
  styleClass="dsm-package-config-dialog"
>
  <ng-template pTemplate="header">
    <div class="flex items-center gap-2 w-full">
      <span class="text-body-lg-600 text-surface-700">
        {{ packageDetail()?.name || 'Package' }}
      </span>
      @if (workingEntries().length > 0) {
        <span class="text-body-lg-600 text-emerald-700">({{ workingEntries().length }})</span>
      }
      @if (hasPackageDiscount()) {
        <span
          class="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold uppercase tracking-wide px-2.5 py-0.5"
        >
          {{ packageDiscountPercentage() }}% OFF
        </span>
      }
      <p-button
        class="ml-auto"
        icon="pi pi-times"
        [rounded]="true"
        [text]="true"
        severity="secondary"
        size="small"
        aria-label="Close"
        (onClick)="closePackageDialog()"
      />
    </div>
  </ng-template>

  <div class="flex flex-col gap-3 max-h-[60vh] overflow-y-auto px-1 py-1">
    @if (packageDetailResource.isLoading()) {
      <div class="flex flex-col items-center justify-center gap-3 text-surface-400 p-8 text-center">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
        <p class="text-body-sm m-0">Loading package details...</p>
      </div>
    } @else if (packageDetailHasError()) {
      <div class="flex flex-col items-center justify-center gap-3 p-8 text-center">
        <i class="pi pi-times-circle text-red-600" style="font-size: 2rem"></i>
        <p class="text-body-sm-500 text-surface-700 m-0">Could not load package details.</p>
        <p-button label="Retry" icon="pi pi-refresh" size="small" [outlined]="true" (onClick)="retryPackageDetail()" />
      </div>
    } @else if (!workingEntries().length) {
      <div class="flex flex-col items-center justify-center gap-3 text-surface-400 p-8 text-center">
        <i class="bi bi-cart-x" style="font-size: 2rem"></i>
        <p class="text-body-sm m-0">No products in this package.</p>
      </div>
    } @else {
      @for (entry of workingEntries(); track entry.id) {
        <dsm-selected-product-panel
          [product]="entry.product"
          [values]="entry.values"
          [addendumId]="addendumId()"
          [showDeleteIcon]="true"
          [showEditIcon]="true"
          [missingFieldsCount]="missingFieldsByEntryId()[entry.id]"
          [discountPercentage]="packageDiscountPercentage()"
          (editRequested)="editingEntryId.set(entry.id)"
          (removeRequested)="removeWorkingEntry(entry.id)"
        />
      }
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="flex items-center justify-between w-full gap-3 flex-wrap">
      @if (attributesLoadFailed()) {
        <div class="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <i class="pi pi-times-circle text-red-600 mt-0.5"></i>
          <span class="flex-1 text-sm text-red-900">Could not load attribute requirements.</span>
          <p-button
            label="Retry"
            icon="pi pi-refresh"
            size="small"
            severity="danger"
            [text]="true"
            (onClick)="retryAttributes()"
          />
        </div>
      } @else if (!attributesLoaded() && workingEntries().length > 0) {
        <div class="flex items-center gap-2 text-body-sm text-surface-500">
          <i class="pi pi-spin pi-spinner"></i>
          Checking attribute requirements...
        </div>
      } @else if (unconfigurableCount() > 0) {
        <p-message severity="error" size="small" variant="outlined">
          {{ unconfigurableCount() }} option{{ unconfigurableCount() === 1 ? '' : 's' }} cannot be added because
          attribute selection is required but attributes are missing. Remove
          {{ unconfigurableCount() === 1 ? 'it' : 'them' }} to add the rest of the package.
        </p-message>
      } @else if (totalMissingFields() > 0) {
        <p-message severity="error" size="small" variant="outlined">
          {{ totalMissingFields() }} missing field{{ totalMissingFields() === 1 ? '' : 's' }}, configure products to add
          the package.
        </p-message>
      } @else if (workingEntries().length > 0) {
        <div class="flex items-center gap-1.5">
          <span class="text-body-sm text-surface-700">Total:</span>
          @if (hasPackageDiscount()) {
            <span class="text-body-sm text-surface-400 line-through">{{
              packageOriginalTotal() | currencyFormat
            }}</span>
            <span class="text-body-lg-700 text-emerald-700">{{ packageTotal() | currencyFormat }}</span>
          } @else {
            <span class="text-body-lg-700 text-emerald-700">{{ packageTotal() | currencyFormat }}</span>
          }
        </div>
      } @else {
        <span></span>
      }
      <div class="flex items-center gap-2 ms-auto">
        <p-button label="Cancel" severity="secondary" type="button" (onClick)="closePackageDialog()" />
        <p-button label="Add Package" type="button" (onClick)="addPackage()" [disabled]="packageAddBlocked()" />
      </div>
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="editDialogVisible()"
  (visibleChange)="$event || editingEntryId.set(null)"
  [modal]="true"
  [header]="editingProduct()?.code || ''"
  [style]="{ width: '36rem', maxWidth: '95vw' }"
  [draggable]="false"
  [resizable]="false"
  [baseZIndex]="12000"
  appendTo="body"
>
  @if (editingProduct(); as product) {
    <dsm-selected-product-config-form
      [product]="product"
      [initialValues]="editingValues()"
      [addendumId]="addendumId()"
      (commit)="onEntryFormCommit($event)"
    />
  }
  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" type="button" (onClick)="editingEntryId.set(null)" />
    <p-button
      label="Update Option"
      type="button"
      [disabled]="editBlockedByMissingAttributes()"
      (onClick)="confirmEditing()"
    />
  </ng-template>
</p-dialog>
